import React, { useEffect, useRef, useState } from "react";
import {
    Card,
    Table,
    Spinner,
    Alert,
    Row,
    Col,
    Badge,
    Button,
} from "react-bootstrap";
import API from "../../services/api";
import TodaysOrdersGrid from "./TodaysOrdersGrid";

const PendingOrdersGrid = () => {
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [ordersError, setOrdersError] = useState("");
    const [soundEnabled, setSoundEnabled] = useState(false);
    const notificationSound = useRef(null);
    const lastDataRef = useRef([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // 🧩 Spice Levels
    const SPICE_LEVELS = [
        { value: "mild", label: "1 マイルド (MILD)" },
        { value: "medium", label: "2 普通 (MEDIUM)" },
        { value: "hot", label: "3 中辛 (HOT)" },
        { value: "very_hot", label: "4 大辛 (VERY HOT)" },
        { value: "super_hot", label: "5 激辛 (SUPER HOT)" },
    ];

    const getSpiceLabel = (level) =>
        SPICE_LEVELS.find((s) => s.value === level)?.label || "";
    const renderSpiceChili = (level) => {
        const icons = {
            mild: "🌶️",
            medium: "🌶️🌶️",
            hot: "🌶️🌶️🌶️",
            very_hot: "🌶️🌶️🌶️🌶️",
            super_hot: "🌶️🌶️🌶️🌶️🌶️",
        };
        return icons[level] || "";
    };

    const getTableLabel = (tableNum) => {
        return tableNum == 20 ? "Takeout" : 'Table:' + tableNum;
    };

    // 🕹 Enable Sound Interaction
    const enableSound = () => {
        setSoundEnabled(true);
        if (notificationSound.current) {
            notificationSound.current.loop = true;
            notificationSound.current
                .play()
                .then(() => {
                    notificationSound.current.pause();
                    notificationSound.current.currentTime = 0;
                })
                .catch(() =>
                    console.warn("⚠️ Autoplay blocked until interaction")
                );
        }
    };

    // 🔔 Initialize sound
    useEffect(() => {
        notificationSound.current = new Audio("/sound/alert.wav");
        notificationSound.current.loop = true;
    }, []);

    // 🧭 Fetch pending orders
    const fetchOrders = async () => {
        try {
            const res = await API.get("/orders?status=pending");
            const newData = res.data.data || [];

            setOrders(newData);
            lastDataRef.current = newData;

            // 🔊 Play sound continuously if pending orders exist
            if (soundEnabled && notificationSound.current) {
                if (newData.length > 0) {
                    notificationSound.current.play().catch(() => {
                        console.warn("⚠️ Autoplay blocked until interaction.");
                    });
                } else {
                    notificationSound.current.pause();
                    notificationSound.current.currentTime = 0;
                }
            }
        } catch (err) {
            console.error(err);
            setOrdersError("⚠️ Failed to fetch pending orders.");
        } finally {
            setLoadingOrders(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        const interval = setInterval(fetchOrders, 10000);
        return () => clearInterval(interval);
    }, [soundEnabled]);

    // 🧾 Update order status
    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            if (notificationSound.current) {
                notificationSound.current.pause();
                notificationSound.current.currentTime = 0;
            }

            await API.put(`/orders/${orderId}/status`, { status: newStatus });
            setOrders((prev) => prev.filter((o) => o.id !== orderId));

            // 🌀 Notify today's grid to refresh
            setRefreshTrigger((prev) => prev + 1);
        } catch (err) {
            console.error("❌ Failed to update order:", err);
        }
    };


    // 🖨 Thermal Print Handler (88 mm optimized)
    // FIX: Use an HTML table for receipt items and right-align the price column so
    // prices are visually aligned when printed on the thermal printer.
    const handlePrint = (order) => {
        if (notificationSound.current) {
            notificationSound.current.pause();
            notificationSound.current.currentTime = 0;
        }

        const total = order.items.reduce(
            (sum, i) => sum + i.total_price,
            0
        );

        // Build receipt HTML rows
        const receiptRows = order.items
            .map((item) => {
                const itemTotal = (item.price * item.quantity).toFixed(0);

                // Sub-items (if any) are rendered as separate rows with indentation
                const subRows = (item.meta?.slots || [])
                    .map((s) => {
                        const addPrice = s.additional_price > 0 ? ` + ¥${s.additional_price}` : "";
                        const spice = s.spice_level ?
                            `(${getSpiceLabel(s.spice_level)})` : "";
                        const price = s.additional_price > 0 ? `¥${(s.additional_price).toFixed(0)}` : "";
                        return `
                            <tr>
                              <td style=\"padding-left:12px; font-size:14px;\">• ${s.food_name}${spice}${addPrice ? ` ${addPrice}` : ""}</td>
                              <td style=\"text-align:center; \">-</td>
                              <td style=\"text-align:right; \">${price}</td>
                            </tr>`;
                    })
                    .join("");

                const spice = item.spice_level ? ` ${renderSpiceChili(item.spice_level)} (${getSpiceLabel(item.spice_level)})` : "";

                return `
                    <tr>
                      <td style=\"font-size:14px; font-weight:bold;\">${item.title}${spice}</td>
                      <td style=\"text-align:center; font-weight:bold; white-space:nowrap;\">x${item.quantity}</td>
                      <td style=\"text-align:right; white-space:nowrap;\">¥${itemTotal}</td>
                    </tr>
                    ${subRows}
                `;
            })
            .join("\n");

        const printContent = `
      <html>
        <head>
          <title>Order #${order.id}</title>
          <meta name=\"viewport\" content=\"width=device-width,initial-scale=1\" />
          <script src=\"https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js\"></script>
          <style>
            /* Thermal-friendly, fixed width */
            body { font-family: monospace; font-size: 14px; width: 350px; margin: 0; padding: 12px; line-height: 1.4; }
            .center { text-align: center; }
            table.receipt { width: 100%; border-collapse: collapse; margin-top: 6px; }
            table.receipt td, table.receipt th { padding: 4px 0; }
            table.receipt td:nth-child(1) { width: 62%; }
            table.receipt td:nth-child(2) { width: 18%; text-align:center; }
            table.receipt td:nth-child(3) { width: 20%; text-align:right; }
            hr.dashed { border:0; border-top:1px dashed #000; margin:6px 0; }
            .total { font-weight:bold; font-size:16px; text-align:right; }
            /* Prevent line-wrapping of amounts */
            td { white-space: nowrap; }
             @page { margin: 0; }
             body { margin: 0; }
          </style>
        </head>
        <body>
          <div class=\"center\">
          <h2 style="position: absolute; top: -5px; left: 0;">${getTableLabel(order.table_number)}</h2>
            <h3 style=\"margin:0;\">🍛 KITCHEN ORDER</h3>
<!--            <div style="font-size:24px">Order #<span style=\"text-align:left; font-size:16px\">${order.id}</span></div>-->
            <div> <span style=\"font-size:24px; font-weight: bold\">Order # ${order.id}</span></div>
<!--            <div> <span style=\"font-size:18px; font-weight: bold\">${getTableLabel(order.table_number)}</span></div>-->
           <div>${new Date(order.created_at).toLocaleString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })}</div>
            <svg id=\"barcode\"></svg>
          </div>

          <hr class=\"dashed\"/>

          <table class=\"receipt\">
            <thead>
              <tr>
                <th style=\"text-align:left; font-size:12px\">Item</th>
                <th style=\"text-align:center; font-size:12px\">Qty</th>
                <th style=\"text-align:right; font-size:12px\">Price</th>
              </tr>
            </thead>
            <tbody>
              ${receiptRows}
            </tbody>
          </table>

          <hr class=\"dashed\"/>

          <div class=\"total\">TOTAL: ¥${total.toFixed(0)}</div>

          <hr class=\"dashed\"/>

          <div style=\"text-align:center; margin-top:8px;\">Thank you 🙏</div>

          <script>
            window.onload = function() {
              // Use order.id for barcode to better identify receipts, but you can switch back to table_number if preferred
               JsBarcode("#barcode", "${order.table_number},${order.id}", {
                format: "CODE128",
                width: 1.2,
                height: 50,
                displayValue: false,
                margin: 0
              });

              // Give the browser a short moment to render the table before printing
              setTimeout(function() {
                window.print();
              }, 120);

              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;

        const printWindow = window.open("", "", "width=400,height=800");
        if (printWindow) {
            printWindow.document.open();
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.onafterprint = () => {
                updateOrderStatus(order.id, "preparing");
                setRefreshTrigger((prev) => prev + 1); // 🌀 Refresh today orders
            };
        }
    };

    // 🧭 UI States
    if (loadingOrders)
        return (
            <div className="text-center">
                <Spinner animation="border" /> Loading orders...
            </div>
        );
    if (ordersError) return <Alert variant="danger">{ordersError}</Alert>;

    // 🧱 Layout
    return (
        <>
            {!soundEnabled && (
                <div className="text-center mb-3">
                    <Button variant="success" size="sm" onClick={enableSound}>
                        🔔 Enable Sound Alerts
                    </Button>
                </div>
            )}

            { orders.length > 0 && <Row className="g-3">
                {orders.map((order) => (
                    <Col md={6} lg={4} key={order.id}>
                        <Card className="shadow-sm h-100">
                            <Card.Body>
                                <div className="d-flex justify-content-between mb-2 align-items-center">
                                    <strong>Order #{order.id}</strong>
                                    <Badge bg="warning" text="dark">
                                        {order.status}
                                    </Badge>
                                </div>

                                <div className="mb-2 text-muted">
                                    🕒 {new Date(order.created_at).toLocaleTimeString()} — <strong>{getTableLabel(order.table_number)}</strong>
                                </div>

                                <Table striped bordered hover size="sm">
                                    <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Sub-items</th>
                                        <th>Qty</th>
                                        <th>Price</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {order.items.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                {item.title} {item.spice_level && (
                                                <span style={{ color: "#ff4500", fontSize: "0.85rem" }}>
                                                      {renderSpiceChili(item.spice_level)} ({getSpiceLabel(item.spice_level)})
                                                    </span>
                                            )}
                                            </td>
                                            <td>
                                                {item.meta?.slots?.length > 0 ? (
                                                    <ul className="mb-0 p-0" style={{ listStyle: "none" }}>
                                                        {item.meta.slots.map((s, i) => (
                                                            <li key={i}>
                                                                {s.food_name} {s.spice_level && (
                                                                <span style={{ color: "#ff4500", fontSize: "0.8rem" }}>
                                                                      {renderSpiceChili(s.spice_level)} ({getSpiceLabel(s.spice_level)})
                                                                    </span>
                                                            )} {s.additional_price > 0 && `( +¥${s.additional_price})`}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td>{item.quantity}</td>
                                            <td>¥{(item.total_price).toFixed(0)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>

                                <div className="text-end mt-2 mb-2">
                                    <strong>
                                        Total: ¥
                                        {order.items
                                            .reduce(
                                                (sum, item) => sum + item.total_price,
                                                0
                                            )
                                            .toFixed(0)}
                                    </strong>
                                </div>

                                <div className="text-end d-flex justify-content-end gap-3 mt-3">
                                    <Button
                                        variant="success"
                                        className="fw-bold fs-5 px-5 py-3"
                                        onClick={() =>
                                            updateOrderStatus(order.id, "preparing")
                                        }
                                    >
                                        🍳 Prepare
                                    </Button>

                                    <Button
                                        variant="primary"
                                        className="fw-bold fs-5 px-5 py-3"
                                        onClick={() => handlePrint(order)}
                                    >
                                        🖨 Print
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>}

            <TodaysOrdersGrid
                renderSpiceChili={renderSpiceChili}
                getSpiceLabel={getSpiceLabel}
                refreshTrigger={refreshTrigger}
                handlePrint={handlePrint}
            />
        </>
    );
};

export default PendingOrdersGrid;
